import React from 'react';
import { Helmet } from 'react-helmet';

import AuthorCredit from '../../components/ui/AuthorCredit';
import FreshnessDate, { getFreshnessDateIso } from '../../components/ui/FreshnessDate';
import GEO_FAQ from '../../components/ui/GEO_FAQ';
import { DIGITAL_LINKS, POSITIONING } from '../../compliance/mtdFacts';

export default function DigitalLinksExplained() {
  const schema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "MTD Digital Links Explained",
    "dateModified": getFreshnessDateIso()
  };

  const faqs = [
    {
      question: "Is copy and pasting data between spreadsheets legal under MTD?",
      answer: DIGITAL_LINKS.summary,
      hmrc_manual_ref: DIGITAL_LINKS.sourceAlias,
      hmrc_manual_url: DIGITAL_LINKS.source
    },
    {
      question: "What constitutes a valid digital link for MTD for Income Tax?",
      answer: DIGITAL_LINKS.photoNote,
      hmrc_manual_ref: DIGITAL_LINKS.sourceAlias,
      hmrc_manual_url: DIGITAL_LINKS.source
    }
  ];

  return (
    <div className="min-h-screen bg-slate-950 text-white font-sans selection:bg-indigo-500/30">
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(schema)}</script>
        <title>Making Tax Digital Links Explained | Zap Expenses</title>
        <meta name="description" content="What is a digital link under MTD for Income Tax? Understanding the HMRC data-transfer rules and how compatible software keeps you on track." />
        <meta name="keywords" content="MTD digital link, HMRC data transfer rules, MTD ITSA compliance, bridging software UK, digital tax record" />
      </Helmet>

      <div className="max-w-4xl mx-auto py-16 px-6">

        <FreshnessDate className="mb-4" />
        <h1 className="text-4xl md:text-5xl font-extrabold tracking-tight mb-4">
          Digital Links Explained: The <span className="text-red-400">Copy-Paste Rule</span>
        </h1>

        <AuthorCredit />

        <div className="bg-indigo-500/10 border border-indigo-500/30 p-6 rounded-2xl mb-6 shadow-lg">
          <p className="text-xl font-medium leading-relaxed">
            <strong>{DIGITAL_LINKS.summary}</strong>
          </p>
        </div>
        <div className="bg-red-500/10 border border-red-500/30 p-4 rounded-xl text-left text-sm text-slate-300 w-full mb-12">
          <strong className="text-red-400 block mb-1">Non-Compliance Risk:</strong>
          "Standard HMRC penalties for 'Careless' inaccuracies start at 30% of the potential lost tax if prompted by an enquiry."
          <div className="text-[10px] text-slate-500 italic mt-2 border-t border-red-500/20 pt-2">
            Source: <a href="https://www.gov.uk/government/publications/compliance-checks-penalties-for-inaccuracies-in-returns-or-documents-ccfs7a" target="_blank" rel="noopener noreferrer" className="underline hover:text-red-400">HMRC, Penalties for Inaccuracies</a>
          </div>
        </div>

        <div className="prose prose-invert prose-lg max-w-none">
          <h2 className="text-2xl font-bold mb-4">HMRC's digital records guidance</h2>
          <p>
            Under <strong>Making Tax Digital for Income Tax</strong>, a digital record is created and stored in software that works with MTD for Income Tax.
            If you use more than one product, those products must be digitally linked. {DIGITAL_LINKS.note}
          </p>
          <p>
            Snapping a photo and sending it via <strong>WhatsApp</strong> to Zap Expenses stores the original and extracts fields for review.
            {DIGITAL_LINKS.photoNote} {POSITIONING.noSubmit}
          </p>

          <GEO_FAQ faqs={faqs} />

          <p className="mt-8">
            <a href={DIGITAL_LINKS.source} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-indigo-400 hover:text-indigo-300 transition-colors font-bold">
              {DIGITAL_LINKS.sourceAlias} →
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}
